import {
  getVrVulnerabilities,
  SelectedValueReportingFacet,
  VrVulnerabilitiesFacetValue,
  VrVulnerabilitiesQueryData,
  VrVulnerabilitiesResolverResponse,
  VrAggregationQueryArgs
} from '../../../../api/queries/value-reporting/vr-vulnerabilities'
import {
  VulnerabilitySeverity,
  VulnerabilityStatus
} from '../../../../graphql/schema/shared'
import { Query } from '../../../../hooks/queries'
import { useContrastQuery } from '../../../../hooks/use-contrast-query'
import { HistogramData } from './shared'

export interface VrVulnerabilitiesLoaderData {
  readonly totalVulnerabilities: number
  readonly facets: {
    readonly vulnerabilitySeverity: HistogramData<VulnerabilitySeverity>
    readonly vulnerabilityStatus: HistogramData<VulnerabilityStatus>
    readonly vulnerabilityType: HistogramData<string>
  }
}

type VrVulnerabilitiesMappingKey =
  | 'vulnerabilitySeverity'
  | 'vulnerabilityStatus'
  | 'vulnerabilityType'

const mapValues = (
  key: VrVulnerabilitiesMappingKey,
  facetValue: VrVulnerabilitiesFacetValue[]
) =>
  facetValue.reduce(
    (prev, curr) => ({
      items: [
        ...prev.items,
        key === 'vulnerabilityType'
          ? { displayName: curr.displayName, value: curr.value }
          : curr.value
      ],
      counts: [...prev.counts, curr.count]
    }),
    {
      items: [],
      counts: []
    }
  )

const mapFor = (
  key: VrVulnerabilitiesMappingKey,
  data: VrVulnerabilitiesQueryData['valueReportingVulnerabilities']['facets']
) => mapValues(key, data[key])

export const mapData = (
  data: VrVulnerabilitiesQueryData['valueReportingVulnerabilities']['facets']
) => ({
  vulnerabilitySeverity: mapFor('vulnerabilitySeverity', data),
  vulnerabilityStatus: mapFor('vulnerabilityStatus', data),
  vulnerabilityType: mapFor('vulnerabilityType', data)
})

export function useVrVulnerabilities(
  organizationUuid: string,
  selectedFacets?: SelectedValueReportingFacet[]
) {
  const { data, isLoading, error } = useContrastQuery<
    VrAggregationQueryArgs,
    VrVulnerabilitiesResolverResponse
  >(
    [Query.VR_VULNERABILITIES, organizationUuid, selectedFacets],
    getVrVulnerabilities,
    {
      organizationUuid,
      selectedFacets
    }
  )

  return {
    data: {
      totalVulnerabilities: data ? data.totalVulnerabilities : 0,
      facets: data ? mapData(data.facets) : {}
    },
    isLoading,
    error
  }
}
